import { APIScope, GlobalEvents, InstanceAPI, LayerInstance } from '@/api/internal';
import { useMaptipStore } from '@/stores/maptip';
import type { Attributes, GraphicHitResult, MaptipProperties, Point, ScreenPoint } from '@/geo/api';
import { GeometryType } from '@/geo/api';

export class MaptipAPI extends APIScope {
    maptipStore: any;

    /**
     * @constructor
     * @param {InstanceAPI} iApi the RAMP instance
     */
    constructor(iApi: InstanceAPI) {
        super(iApi);
        this.maptipStore = useMaptipStore(this.$vApp.$pinia);
    }

    // # makes variables private outside of typescript and lets us hide things on the API
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Classes/Private_class_fields
    #lastHit: GraphicHitResult | undefined = undefined;
    #currentCheck: ScreenPoint | undefined = undefined;

    /**
     * Checks for a graphic at the given screen coordinates.
     * On a graphic hit the point is put in the maptip store and the `map/graphichit` event is fired.
     *
     * @param {ScreenPoint} screenPoint The screen coordinates for the hitTest
     * @returns {Promise<void>} resolves after the event is fired or no new graphic is hit.
     */
    async checkAtCoord(screenPoint: ScreenPoint): Promise<void> {
        this.#currentCheck = screenPoint;

        // Get the graphic object
        const graphicHit = await this.$iApi.geo.map.getGraphicAtCoord(screenPoint);

        // cancel if new check came in while waiting for `getGraphicAtCoord`
        // If the same point is checked twice technically it can get out of sync but then we're checking the same point anyways
        if (this.#currentCheck !== screenPoint) {
            return;
        }

        if (!graphicHit) {
            this.clear();
            // If there is no graphic hit, disable the maptip. Otherwise vue-tippy will
            // show the last maptip content for the previous graphic hit on touch devices when they pan/zoom.
            this.maptipStore.maptipInstance.disable();
            return;
        }
        // Get the layer
        const layerInstance = graphicHit.layer ?? this.$iApi.geo.layer.getLayer(graphicHit.layerId);

        if (layerInstance?.geomType != GeometryType.POLYGON) {
            // Check if the same maptip already exists
            if (
                this.#lastHit &&
                this.#lastHit.layerId === graphicHit.layerId &&
                this.#lastHit.oid === graphicHit.oid &&
                this.#lastHit.layerIdx === graphicHit.layerIdx
            ) {
                // Same maptip, no need for changes
                // This keeps the maptip in place and saves some trips to Vuex store
                return;
            }
        }

        this.clear();
        this.#lastHit = graphicHit;

        if (!layerInstance) {
            // Something seriously wrong here because esri gave us a non-existent layerID
            console.error(`graphic hit test returned non-existent layer id: ${graphicHit.layerId}`);
            return;
        }

        // remove this once we support maptips on RAMP graphics
        if (!layerInstance.maptips) {
            // the hit layer doesn't support maptips
            return;
        }

        // Get the icon svg string for the graphic
        const icon: string = await layerInstance.getIcon(graphicHit.oid);

        // get the attributes for the graphic
        const graphic = await layerInstance.getGraphic(graphicHit.oid, {
            getAttribs: true
        });

        // At this point we have a graphic hit and a layer, so we can show the maptip
        this.maptipStore.maptipInstance.enable();
        this.setPoint(this.$iApi.geo.map.screenPointToMapPoint(screenPoint));

        this.$iApi.event.emit(GlobalEvents.MAP_GRAPHICHIT, {
            layer: layerInstance,
            graphicHit,
            attributes: graphic.attributes,
            icon,
            screenPoint
        });
    }

    /**
     * Generates and sets the "default" maptip.
     *
     * @param info the maptip info payload
     */
    generateDefaultMaptip(info: {
        layer: LayerInstance;
        graphicHit: GraphicHitResult;
        attributes: Attributes;
        icon: string;
        screenPoint: Point;
    }) {
        this.setContent(
            `<div class="flex items-center space-x-5"><span>${info.icon}</span><span class="line-clamp-3">${info.layer.maptipValue(
                info.attributes
            )}</span></div>`
        );
    }

    /**
     * Clears the maptip from the map
     */
    clear(): void {
        this.#lastHit = undefined;
        this.maptipStore.setMaptipPoint(undefined);
        this.maptipStore.setMaptipContent('');
    }

    /**
     * Get the `tippy` maptip instance
     * Documentation: https://kabbouchi.github.io/tippyjs-v4-docs/tippy-instance/
     *
     * @returns {any} the `tippy` maptip instance
     */
    getInstance(): any {
        return this.maptipStore.maptipInstance;
    }

    /**
     * Get the current point for the maptip
     *
     * @returns {Point} the current maptip map point
     */
    getPoint(): MaptipProperties | undefined {
        return this.maptipStore.maptipPoint;
    }

    /**
     * Set the current maptip point. Undefined = maptip wont be shown.
     *
     * @param {Point | undefined} maptipPoint
     */
    setPoint(maptipPoint: Point): void {
        this.maptipStore.setMaptipPoint(maptipPoint);
    }

    /**
     * Set the html string for the maptip
     * If empty string is provided, the maptip will use the default content
     *
     * @param {string} content the new maptip html content
     */
    setContent(content: string): void {
        this.maptipStore.setMaptipContent(content);
    }
}
